/**
 * Utilities for classroom code generation and date scheduling
 */

/**
 * Extracts a sensible 2-4 uppercase prefix based on the center name
 * e.g., "Trung Tâm Sao Việt – Đồng Nai" -> "DN"
 * e.g., "Trung Tâm Sao Việt - Thủ Dầu Một (Bình Dương)" -> "TDM"
 */
export const getCenterPrefix = (centerName = '') => {
  if (!centerName) return 'SV';
  const nameLower = centerName.toLowerCase();
  if (nameLower.includes('đồng nai')) return 'DN';
  if (nameLower.includes('thủ dầu một')) return 'TDM';
  if (nameLower.includes('bình dương')) return 'BD';
  if (nameLower.includes('hồ chí minh') || nameLower.includes('hcm')) return 'HCM';
  if (nameLower.includes('hà nội')) return 'HN';
  if (nameLower.includes('đà nẵng')) return 'DNA';
  if (nameLower.includes('cần thơ')) return 'CT';
  if (nameLower.includes('vũng tàu')) return 'VT';
  if (nameLower.includes('hải phòng')) return 'HP';

  // Strip generic branding words
  const clean = centerName
    .replace(/trung\s+tâm|sao\s+việt|chi\s+nhánh|cơ\s+sở/gi, '')
    .trim()
    .replace(/^[\s–—\-:.]+|[\s–—\-:.]+$/g, '')
    .trim();

  const words = clean.split(/\s+/).filter(Boolean);
  if (words.length >= 2) {
    return words.slice(0, 3).map((w) => w[0]).join('').toUpperCase();
  }
  if (words.length === 1 && words[0].length >= 2) {
    return words[0].slice(0, 3).toUpperCase();
  }
  return 'SV';
};

/**
 * Generates a clean, unique random classroom code
 * e.g. "DN-K8492" or "TDM-K3914"
 */
export const generateRandomClassCode = (centerName = '', existingCodes = []) => {
  const prefix = getCenterPrefix(centerName);
  const existingSet = new Set((existingCodes || []).map((c) => String(c || '').trim().toUpperCase()));

  for (let i = 0; i < 30; i += 1) {
    const randomDigits = Math.floor(1000 + Math.random() * 9000);
    const candidate = `${prefix}-K${randomDigits}`;
    if (!existingSet.has(candidate)) {
      return candidate;
    }
  }

  // Fallback with larger number
  return `${prefix}-K${Math.floor(10000 + Math.random() * 90000)}`;
};

/**
 * Returns formatted ISO datetime-local strings for today (08:00) and +1 month (23:59)
 */
export const getInitialClassDates = () => {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  const startStr = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T08:00`;

  const nextMonth = new Date(now);
  nextMonth.setMonth(nextMonth.getMonth() + 1);
  const endStr = `${nextMonth.getFullYear()}-${pad(nextMonth.getMonth() + 1)}-${pad(nextMonth.getDate())}T23:59`;

  return { starts_at: startStr, ends_at: endStr };
};

/**
 * Computes an end date string by adding N months to startsAtStr
 */
export const calculateEndDate = (startsAtStr, months) => {
  if (months === 0 || !months) return '';
  const base = startsAtStr ? new Date(startsAtStr) : new Date();
  const target = new Date(base);
  target.setMonth(target.getMonth() + months);
  const pad = (n) => String(n).padStart(2, '0');
  return `${target.getFullYear()}-${pad(target.getMonth() + 1)}-${pad(target.getDate())}T23:59`;
};
